<template>
  <div class="bg-white min-h-screen">
    <!-- Hero Banner Section -->
    <section class="bg-gradient-to-br from-[#CB2530] to-[#B41923] relative overflow-hidden pt-24 lg:pt-32 pb-12 lg:pb-16 min-h-[320px] lg:min-h-[400px] flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 w-full">
        <h1 class="text-4xl md:text-5xl font-medium text-white mb-6 tracking-tight leading-tight">{{ $t('common.product.specsTitle') }}</h1>
        <div class="max-w-3xl mx-auto">
          <p class="text-xl text-white/85 leading-relaxed">
            {{ $t('common.product.specsDesc') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Navigation Tabs -->
    <section class="sticky top-[72px] z-30 bg-white/80 backdrop-blur-md border-b border-slate-200 shadow-sm">
      <div class="max-w-7xl mx-auto px-4 py-4 flex justify-center space-x-6 lg:space-x-12 overflow-x-auto scrollbar-hide">
        <button v-for="tab in displayTabs" :key="tab.id" @click="scrollTo(tab.id)" 
                class="text-sm lg:text-base font-medium transition-colors hover:text-[#C6202B] whitespace-nowrap"
                :class="activeTab === tab.id ? 'text-[#C6202B]' : 'text-slate-600'">
          {{ tab.name }}
        </button>
      </div>
    </section>

    <!-- Dynamic Tables -->
    <section v-for="table in visibleTables" :key="table.tableKey" :id="table.tableKey" class="py-20 odd:bg-white even:bg-slate-50 border-b border-slate-100">
      <div class="container-custom">
        <div class="text-center mb-12 space-y-4">
          <h2 class="text-3xl text-slate-900 mb-4 tracking-tight font-medium">
            {{ $i18n.locale === 'en' ? table.tableTitleEn || table.tableTitle : table.tableTitle }}
          </h2>
          <p v-if="table.tableSubtitle && !isUnwantedSubtitle(table)" class="text-slate-600 text-base">
            {{ $i18n.locale === 'en' ? table.tableSubtitleEn || table.tableSubtitle : table.tableSubtitle }}
          </p>
        </div>
      
      <div class="relative group">
        <!-- Mobile Scroll Hint -->
        <div class="lg:hidden absolute right-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none animate-bounce-horizontal opacity-80" v-if="!table.scrolled">
          <div class="bg-black/50 backdrop-blur-sm text-white p-2 rounded-full shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>
        </div>

        <div class="overflow-auto max-h-[70vh] lg:max-h-none scroll-smooth rounded-2xl border border-slate-200 shadow-xl bg-white relative group" @scroll="handleTableScroll($event, table)">
          <table class="w-full border-separate border-spacing-0 relative text-sm table-auto table-responsive-scaling table-zebra">
          <thead class="sticky top-0 z-20" style="background: linear-gradient(to right, #C6202B, #8E1219)">
            <!-- 动态渲染多级表头 -->
            <tr v-for="(row, rIdx) in getTableHeaderInfo(table).headerRows" 
                :key="'header_row_' + rIdx"
                class="text-white">
                <th v-for="(col, cIdx) in row" 
                    :key="'header_col_' + rIdx + '_' + cIdx" 
                    :colspan="col.colSpan"
                    :rowspan="col.rowSpan"
                    :style="{ 
                      width: formatWidth(col.width),
                      background: 'transparent',
                      transform: 'translateZ(0)',
                      '-webkit-transform': 'translateZ(0)'
                    }"
                    class="px-4 py-3 border-r border-b border-white/30 text-center font-bold last:border-r-0 sticky z-20"
                    :class="[rIdx === 0 ? 'top-0' : '']">
                  <div class="text-sm tracking-wide">{{ $i18n.locale === 'en' ? col.enTitle || col.title : col.title }}</div>
                </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200">
            <tr v-for="(row, rIdx) in getTableHeaderInfo(table).config.data" :key="rIdx" class="hover:bg-[#FDF3F4]/70 transition-colors group">
              <template v-for="(col, cIdx) in getTableHeaderInfo(table).flattenedCols">
                <td v-if="shouldShowCell(rIdx, cIdx, getTableHeaderInfo(table).flattenedCols, getTableHeaderInfo(table).config.cellConfigs)"
                    :key="rIdx + '_' + cIdx"
                    :rowspan="getCellSpan(rIdx, col, getTableHeaderInfo(table).config.cellConfigs).rowspan"
                    :colspan="getCellSpan(rIdx, col, getTableHeaderInfo(table).config.cellConfigs).colspan"
                    class="px-4 py-2.5 border-r border-slate-200 text-center text-slate-700 font-normal leading-relaxed last:border-r-0"
                    :style="{ color: normalizeColor(col.color), width: formatWidth(col.width) }"
                    :class="{'text-slate-900 font-medium': col.field === 'label' || col.field === 'property' || cIdx === 0}">
                  <div v-html="formatCellValue(row[col.field])"></div>
                </td>
              </template>
            </tr>
          </tbody>
          </table>
      </div>
    </div>
  </div>
  </section>
</div>
</template>

<script>
import { getPublicSpecsList } from '@/api/specs'
import { normalizeTableColor } from '@/utils/brandColor'

export default {
  name: 'SpecsView',
  data() {
    return {
      activeTab: '',
      tableList: [],
      renderLimit: 1, // 新增：控制初始渲染的表格数量
      // 优化：缓存计算后的表头数据
      cachedHeaders: new Map() 
    }
  },
  computed: {
    displayTabs() {
      return this.tableList.map(item => ({
        id: item.tableKey,
        name: this.$i18n.locale === 'en' ? item.tableTitleEn || item.tableTitle : item.tableTitle
      }))
    },
    // 优化：分批显示表格，避免一次性渲染所有 DOM
    visibleTables() {
      return this.tableList.slice(0, this.renderLimit);
    }
  },
  created() {
    this.fetchData()
    this.$root.$on('language-changed', this.fetchData)
  },
  beforeDestroy() {
    this.$root.$off('language-changed', this.fetchData)
  },
  methods: {
    normalizeColor(color) {
      return normalizeTableColor(color)
    },
    async fetchData() {
      try {
        const res = await getPublicSpecsList()
        if (res.code === 200) {
          // 过滤掉“产品类别”表格，该表格可能在其他页面展示
          const filteredData = res.data.filter(item => item.tableKey !== 'product-type')
          this.tableList = filteredData.map(item => ({
            ...item,
            scrolled: false // 初始化滚动状态
          }))
          if (this.tableList.length > 0) {
            this.activeTab = this.tableList[0].tableKey
          }
          
          // 预计算所有表格的表头，避免在渲染循环中重复计算
          this.precomputeHeaders();
          
          // 渲染完第一个表格后，分批渲染后续表格
          this.startIncrementalRendering();
        }
      } catch (error) {
        console.error('Failed to fetch specs data:', error)
      }
    },
    precomputeHeaders() {
      this.tableList.forEach(table => {
        const config = this.parseTableConfig(this.$i18n.locale === 'en' ? table.tableConfigEn || table.tableConfig : table.tableConfig);
        const headerRows = this.getHeaderRows(config.columns);
        const flattenedCols = this.getFlattenedColumns(config.columns);
        this.cachedHeaders.set(table.tableKey, { headerRows, flattenedCols, config });
      });
    },
    startIncrementalRendering() {
      if (this.renderLimit < this.tableList.length) {
        // 使用 requestAnimationFrame 或 setTimeout 分批渲染
        setTimeout(() => {
          this.renderLimit += 1;
          this.startIncrementalRendering();
        }, 100); // 每 100ms 多渲染一个表格，给浏览器喘息机会
      }
    },
    getTableHeaderInfo(table) {
      return this.cachedHeaders.get(table.tableKey) || { headerRows: [], flattenedCols: [], config: { data: [], cellConfigs: {} } };
    },
    handleTableScroll(event, table) {
      if (!table.scrolled && event.target.scrollLeft > 20) {
        table.scrolled = true;
        this.$forceUpdate(); // 强制更新以隐藏提示
      }
    },
    isUnwantedSubtitle(table) {
      const unwanted = [
        '导体直径与漆膜厚度对照表',
        'Dimension Comparison Chart'
      ];
      const sub = table.tableSubtitle || '';
      const subEn = table.tableSubtitleEn || '';
      return unwanted.includes(sub) || unwanted.includes(subEn);
    },
    formatWidth(width) {
      if (!width) return 'auto';
      if (typeof width === 'number') return width + 'px';
      if (typeof width === 'string' && /^\d+$/.test(width)) return width + 'px';
      return width;
    },
    formatCellValue(val) {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') {
        return this.$i18n.locale === 'en' ? val.en || val.cn || '' : val.cn || '';
      }
      return val;
    },
    parseTableConfig(config) {
      if (!config) return { columns: [], data: [], cellConfigs: {} };
      try {
        const parsed = typeof config === 'string' ? JSON.parse(config) : config;
        // 优化点 3：使用 Object.freeze 冻结大型静态数据，减少 Vue 响应式系统的追踪开销
        return Object.freeze({
          columns: parsed.columns || [],
          data: parsed.data || [],
          cellConfigs: parsed.cellConfigs || {}
        });
      } catch (e) {
        console.error('Parse table config error:', e);
        return { columns: [], data: [], cellConfigs: {} };
      }
    },
    getHeaderRows(columns) {
      const maxDepth = this.getMaxDepth(columns);
      const rows = Array.from({ length: maxDepth }, () => []);
      
      const traverse = (cols, depth) => {
        cols.forEach(col => {
          const colSpan = this.getColSpan(col);
          
          // 优化：处理“透明”中间层级。如果子项只有一个且标题（中英文）均为空，则父级向下合并
          let extraRows = 0;
          let current = col;
          while (current.children && current.children.length === 1 && 
                 (!current.children[0].title || current.children[0].title.trim() === '') &&
                 (!current.children[0].enTitle || current.children[0].enTitle.trim() === '')) {
            extraRows++;
            current = current.children[0];
          }
          
          const hasChildren = current.children && current.children.length > 0;
          const rowSpan = hasChildren ? (1 + extraRows) : (maxDepth - depth);
          
          rows[depth].push({
            ...col,
            colSpan,
            rowSpan
          });
          
          if (hasChildren) {
            traverse(current.children, depth + 1 + extraRows);
          }
        });
      };
      
      traverse(columns, 0);
      return rows;
    },
    getMaxDepth(columns) {
      let max = 0;
      columns.forEach(col => {
        if (col.children && col.children.length > 0) {
          max = Math.max(max, this.getMaxDepth(col.children));
        }
      });
      return max + 1;
    },
    getColSpan(col) {
      if (!col.children || col.children.length === 0) return 1;
      let span = 0;
      col.children.forEach(child => {
        span += this.getColSpan(child);
      });
      return span;
    },
    getRowSpan(col) {
      // 废弃，逻辑已集成到 getHeaderRows
      return 1;
    },
    hasSubColumns(columns) {
      return columns.some(c => c.children && c.children.length > 0)
    },
    getFlattenedColumns(columns) {
      let leaves = [];
      columns.forEach(c => {
        if (c.children && c.children.length > 0) {
          leaves = leaves.concat(this.getFlattenedColumns(c.children));
        } else {
          leaves.push(c);
        }
      });
      return leaves;
    },
    getCellSpan(rIdx, col, cellConfigs) {
      if (!cellConfigs) return { rowspan: 1, colspan: 1 };
      const configKey = `${rIdx}_${col.field}`;
      const config = cellConfigs[configKey];
      return {
        rowspan: config ? (config.rowspan || 1) : 1,
        colspan: config ? (config.colspan || 1) : 1
      };
    },
    shouldShowCell(rIdx, cIdx, flattenedColumns, cellConfigs) {
      if (!cellConfigs) return true;
      
      for (const key in cellConfigs) {
        const parts = key.split('_');
        if (parts.length < 2) continue;
        
        const configRIdx = parseInt(parts[0]);
        const configField = parts.slice(1).join('_');
        const config = cellConfigs[key];
        
        const rStart = configRIdx;
        const rEnd = rStart + (config.rowspan || 1) - 1;
        
        const configCIdx = flattenedColumns.findIndex(c => c.field === configField);
        if (configCIdx === -1) continue;
        const cStart = configCIdx;
        const cEnd = cStart + (config.colspan || 1) - 1;
        
        if (rIdx >= rStart && rIdx <= rEnd && cIdx >= cStart && cIdx <= cEnd) {
          if (rIdx === rStart && cIdx === cStart) {
            return true;
          }
          return false;
        }
      }
      return true;
    },
    scrollTo(id) {
      this.activeTab = id;
      const element = document.getElementById(id);
      if (element) {
        const offset = 140; // Height of header + sticky tabs
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
    formatCellValue(val) {
      if (val === undefined || val === null || val === '') return '-';
      
      let text = '';
      if (typeof val === 'object') {
        text = this.$i18n.locale === 'en' ? (val.en || val.cn) : (val.cn || val.en);
      } else {
        text = String(val);
      }
      
      if (!text) return '-';
      return text.replace(/\n/g, '<br>');
    }
  }
}
</script>

<style scoped>
.container-custom {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

/* 表格隔行底色：首行白色，偶数行 #FDEDE4 */
.table-zebra tbody tr:nth-child(odd) {
  background-color: #FFFFFF;
}
.table-zebra tbody tr:nth-child(even) {
  background-color: #FDEDE4;
}

/* Custom scrollbar for overflow containers */
.overflow-auto::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}
.overflow-auto::-webkit-scrollbar-track {
  background: rgba(241, 245, 249, 0.5);
  border-radius: 10px;
  margin: 4px;
}
.overflow-auto::-webkit-scrollbar-thumb {
  background: #94A3B8;
  border-radius: 20px;
  border: 2px solid #fff;
  background-clip: padding-box;
  min-height: 40px;
  min-width: 40px;
  box-shadow: 0 0 4px rgba(0,0,0,0.1);
}
.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: #C6202B;
  border-width: 1px;
}
.overflow-auto::-webkit-scrollbar-thumb:active {
  background: #8E1219;
}

/* 方案二：响应式表格缩放 */
@media (max-width: 1440px) {
  .table-responsive-scaling {
    font-size: 0.8rem !important;
  }
  .table-responsive-scaling th, 
  .table-responsive-scaling td {
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }
}

@media (max-width: 1280px) {
  .table-responsive-scaling {
    font-size: 0.75rem !important;
  }
  .table-responsive-scaling th, 
  .table-responsive-scaling td {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
}
</style>
/* Table styles */
th, td {
  border-width: 1px;
}

@keyframes bounce-horizontal {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(10px); }
}
.animate-bounce-horizontal {
  animation: bounce-horizontal 1.5s infinite;
}
</style>
